"use client";
import { useField } from "formik";
import {
  InputHTMLAttributes,
  ReactNode,
  useState,
  useEffect,
  useRef,
} from "react";
import { LockKeyholeIcon } from "lucide-react";
import { Eye } from "../icons/eye";
import { EyeOff } from "../icons/eye-off";
import { inputVariants } from "./text-input";
import { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

interface PasswordInputProps
  extends VariantProps<typeof inputVariants>,
    Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  name: string;
  label: string;
  children?: ReactNode;
  placeholder?: string;
  type?: string;
  showRequirements?: boolean;
}

export const PasswordInput = ({
  name,
  label,
  children,
  size,
  radius,
  variant,
  showRequirements = false,
  ...props
}: PasswordInputProps) => {
  const [field, meta] = useField(name);
  const hasError = meta.touched && meta.error;
  const [showPassword, setShowPassword] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (hasError && inputRef.current) {
      inputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [hasError]);

  // Real-time requirement checks
  const val = field.value || "";
  const requirements = [
    { label: "minimum 8 characters", met: val.length >= 8 },
    { label: "one number", met: /\d/.test(val) },
    { label: "one lowercase letter", met: /[a-z]/.test(val) },
  ];

  return (
    <div className="w-full relative flex flex-col gap-1">
      <div className="flex items-center justify-between w-full">
        <label
          htmlFor={name}
          className="block text-xs md:text-sm lg:text-base text-[#323232]"
        >
          {label}
          {children}
        </label>
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="flex items-center gap-1.5 text-xs focus:outline-none"
        >
          {showPassword ? (
            <>
              <EyeOff className="w-4 h-4" />
              <span>Hide</span>
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" />
              <span>Show</span>
            </>
          )}
        </button>
      </div>

      <div className="relative w-full flex items-center">
        <div className="absolute left-3 text-gray-400 pointer-events-none flex items-center justify-center">
          <LockKeyholeIcon className="w-5 h-5" />
        </div>
        <input
          type={showPassword ? "text" : "password"}
          id={name}
          {...field}
          {...props}
          ref={inputRef}
          className={cn(
            inputVariants({ size, variant, radius }),
            "pl-10 w-full", 
            hasError
              ? "!border-red-500 text-red-500 placeholder:!text-red-500"
              : ""
          )}
        />
      </div>

      {/* Formik Error Message */}
      {hasError && (
        <p className="text-xs text-red-500 mt-0.5">{meta.error}</p>
      )}

      {/* Password Requirements Checklist */}
      {showRequirements && (
        <div className="mt-2 flex flex-row gap-3 text-[10px] lg:text-xs">
          {requirements.map((req, index) => {
            const isTouched = val.length > 0;
            const colorClass = req.met
              ? "text-green-600 font-medium"
              : isTouched
              ? "text-red-500"
              : "text-gray-400";

            return (
              <div
                key={index}
                className={cn(
                  "flex items-center gap-1.5 transition-colors",
                  colorClass
                )}
              >
                <span
                  className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    req.met
                      ? "bg-green-600"
                      : isTouched
                      ? "bg-red-500"
                      : "bg-gray-300"
                  )}
                />
                <span>{req.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};